import { PaginationTracker } from "./pagination-tracker.js";

// Module-level constant for scroll threshold (in pixels)
const SCROLL_THRESHOLD = 100;

/**
 * Extract the newest ID from an array of rows by detecting sort order.
 * Compares first and last item IDs to determine if data is in ASC or DESC order.
 *
 * @param {Array} rows - The array of row objects
 * @returns {number|string|null} The highest ID, or null if no valid ID found
 */
export function getNewestId(rows)
{
	if (!rows || rows.length === 0)
	{
		return null;
	}

	const firstItem = rows[0];
	const lastItem = rows[rows.length - 1];

	// Both items have IDs - compare to detect sort order
	if (firstItem?.id && lastItem?.id)
	{
		// If first ID > last ID, it's DESC order (newest first)
		// If first ID < last ID, it's ASC order (oldest first)
		return firstItem.id > lastItem.id ? firstItem.id : lastItem.id;
	}

	// Only one item has an ID - return whichever exists
	if (firstItem?.id)
	{
		return firstItem.id;
	}

	if (lastItem?.id)
	{
		return lastItem.id;
	}

	return null;
}

/**
 * Get scroll metrics for a container.
 *
 * @param {HTMLElement|globalThis} container
 * @returns {object} Contains scrollTop, clientHeight, and scrollHeight.
 */
export function getScrollMetrics(container)
{
	// @ts-ignore
	const scrollTop = container === globalThis ? globalThis.pageYOffset : container.scrollTop;
	// @ts-ignore
	const clientHeight = container === globalThis ? globalThis.innerHeight : container.clientHeight;
	// @ts-ignore
	const scrollHeight = container === globalThis ? globalThis.document.documentElement.scrollHeight : container.scrollHeight;
	return { scrollTop, clientHeight, scrollHeight };
}

/**
 * Check if the scroll position indicates we should load more items at the bottom.
 *
 * @param {object} metrics - The scroll metrics.
 * @param {number} [threshold=SCROLL_THRESHOLD] - The threshold in pixels.
 * @returns {boolean}
 */
export function shouldLoadMore(metrics, threshold = SCROLL_THRESHOLD)
{
	return metrics.scrollTop + metrics.clientHeight >= metrics.scrollHeight - threshold;
}

/**
 * Check if the scroll position indicates we should load more items at the top.
 *
 * @param {object} metrics - The scroll metrics.
 * @param {number} [threshold=SCROLL_THRESHOLD] - The threshold in pixels.
 * @returns {boolean}
 */
export function shouldLoadAtTop(metrics, threshold = SCROLL_THRESHOLD)
{
	return metrics.scrollTop <= threshold;
}

/**
 * Check if more items can be loaded at the bottom based on metrics and tracker state.
 *
 * @param {object} metrics - The scroll metrics.
 * @param {PaginationTracker} tracker - The pagination tracker.
 * @returns {boolean}
 */
export const canLoad = (metrics, tracker) =>
{
	return shouldLoadMore(metrics) && tracker.canLoadMore();
};

/**
 * Check if more items can be loaded at the top based on metrics and tracker state.
 *
 * @param {object} metrics - The scroll metrics.
 * @param {PaginationTracker} tracker - The pagination tracker.
 * @returns {boolean}
 */
export const canLoadAtTop = (metrics, tracker) =>
{
	return shouldLoadAtTop(metrics) && tracker.canLoadMore();
};

/**
 * Update the rows in the list by appending and update the tracker state.
 *
 * @param {Array} rows
 * @param {PaginationTracker} tracker
 * @param {object} list
 * @param {string|null} lastCursor - The last cursor value.
 * @returns {void}
 */
export const updateRows = (rows, tracker, list, lastCursor = null) =>
{
	if (rows && rows.length > 0)
	{
		list.append(rows);
		tracker.update(rows.length, lastCursor);
	}
	else
	{
		tracker.hasMoreData = false;

		// Let the list decide hasItems based on its actual items.
		// After a reset + empty response, items is [] so hasItems becomes false.
		// After loading items + last page empty, items still has data so hasItems stays true.
		list.updateHasItems();
	}
};

/**
 * Update the rows in the list by prepending older items and update the tracker state.
 * Used when scrolling up to load older items.
 * Preserves scroll position to prevent content jumping.
 *
 * @param {Array} rows
 * @param {PaginationTracker} tracker
 * @param {object} list
 * @param {string|null} lastCursor - The last cursor value.
 * @param {HTMLElement|globalThis|null} [container] - The scroll container.
 * @returns {void}
 */
export const updateRowsAtTop = (rows, tracker, list, lastCursor = null, container = null) =>
{
	if (rows && rows.length > 0)
	{
		// Save scroll position before prepending
		let scrollHeight = 0;
		let scrollTop = 0;

		if (container)
		{
			const metrics = getScrollMetrics(container);
			scrollHeight = metrics.scrollHeight;
			scrollTop = metrics.scrollTop;
		}

		// Prepend the rows
		list.prepend(rows);
		tracker.update(rows.length, lastCursor);

		// Check if we received fewer rows than the limit, indicating no more data
		if (rows.length < tracker.limit)
		{
			tracker.hasMoreData = false;

			// Add a trailing divider to show the date of the oldest items
			if (list.addTrailingDivider)
			{
				list.addTrailingDivider();
			}
		}

		// Restore scroll position to prevent jump.
		// Use onFlush to wait for batched data publishes to complete
		// before measuring the final scroll height. Base 3.6+ batches
		// reactive updates (e.g. hasItems) into microtasks, so the DOM
		// may not be in its final state synchronously after prepend().
		if (container)
		{
			/**
			 * Measures the new scroll height and adjusts scrollTop
			 * by the difference so the viewport stays on the same content.
			 */
			const restoreScrollPosition = () =>
			{
				const newMetrics = getScrollMetrics(container);
				const heightDifference = newMetrics.scrollHeight - scrollHeight;

				if (container === globalThis)
				{
					globalThis.scrollTo(0, scrollTop + heightDifference);
				}
				else
				{
					// @ts-ignore
					container.scrollTop = scrollTop + heightDifference;
				}
			};

			// If the list supports onFlush, wait for batched DOM updates
			if (typeof list.onFlush === 'function')
			{
				list.onFlush(restoreScrollPosition);
			}
			else
			{
				restoreScrollPosition();
			}
		}
	}
	else
	{
		tracker.hasMoreData = false;

		// Let the list decide hasItems based on its actual items.
		list.updateHasItems();

		// Add a trailing divider to show the date of the oldest items
		if (list.addTrailingDivider)
		{
			list.addTrailingDivider();
		}
	}
};

/**
 * Update rows by prepending newer items to the list.
 *
 * @param {Array} rows
 * @param {PaginationTracker} tracker
 * @param {object} list
 * @param {string|number|null} newestId - The ID of the newest item.
 * @returns {void}
 */
export const prependRows = (rows, tracker, list, newestId = null) =>
{
	if (rows && rows.length > 0)
	{
		list.prepend(rows);
		// Update the newest ID to the first item's ID if available
		if (newestId !== null)
		{
			tracker.updateNewest(newestId);
		}
	}
};

/**
 * Set up a fetch callback for loading data.
 *
 * @param {object} data
 * @param {string} [xhrMethod='all'] - The method name to call on data.xhr.
 * @returns {function}
 */
export const setupFetchCallback = (data, xhrMethod = 'all') =>
{
	return (tracker, callback) =>
	{
		/**
		 * This will handle the result of the fetch.
		 *
		 * @param {object|null} response
		 * @returns {void}
		 */
		const resultCallback = (response) =>
		{
			let rows = [];
			let lastCursor = null;
			if (response)
			{
				rows = response.rows || response.items || [];
				lastCursor = response.lastCursor || null;
			}
			callback(rows, lastCursor);
		};

		data.xhr[xhrMethod]('', resultCallback, tracker.currentOffset, tracker.limit, tracker.lastCursor);
	};
};

/**
 * Set up a fetch callback for loading newer data (forward pagination).
 * Uses the 'since' parameter to fetch items newer than the current newest ID.
 *
 * @param {object} data
 * @param {string} [xhrMethod='all'] - The method name to call on data.xhr.
 * @returns {function}
 */
export const setupFetchNewerCallback = (data, xhrMethod = 'all') =>
{
	return (tracker, callback) =>
	{
		/**
		 * This will handle the result of the fetch.
		 *
		 * @param {object|null} response
		 * @returns {void}
		 */
		const resultCallback = (response) =>
		{
			let rows = [];
			let newestId = null;
			if (response)
			{
				rows = response.rows || response.items || [];
				newestId = getNewestId(rows);
			}
			callback(rows, newestId);
		};

		// Pass since as a parameter to the xhr method
		data.xhr[xhrMethod]('', resultCallback, 0, tracker.limit, null, tracker.newestId);
	};
};

/**
 * Fetch and update rows in the list.
 *
 * @param {function} fetchCallback
 * @param {PaginationTracker} tracker
 * @param {object} list
 * @returns {void}
 */
export const fetchAndUpdate = (fetchCallback, tracker, list) =>
{
	fetchCallback(tracker, (rows, lastCursor) =>
	{
		updateRows(rows, tracker, list, lastCursor);
	});
};

/**
 * Fetch and refresh the list.
 *
 * @param {function} fetchCallback
 * @param {PaginationTracker} tracker
 * @param {object} list
 * @returns {void}
 */
export const fetchAndRefresh = (fetchCallback, tracker, list) =>
{
	tracker.reset();
	fetchCallback(tracker, (rows, lastCursor) =>
	{
		list.reset();
		updateRows(rows, tracker, list, lastCursor);

		// Set the newest ID by detecting sort order
		const newestId = getNewestId(rows);
		if (newestId !== null)
		{
			tracker.updateNewest(newestId);
		}
	});
};

/**
 * Fetch and prepend newer items to the list.
 * This is designed to be called manually (e.g., via timer/polling) rather than on scroll.
 *
 * @param {function} fetchNewerCallback
 * @param {PaginationTracker} tracker
 * @param {object} list
 * @returns {void}
 */
export const fetchAndPrepend = (fetchNewerCallback, tracker, list) =>
{
	if (!tracker.canLoadNewer())
	{
		return;
	}

	if (tracker.loadingNewer)
	{
		return;
	}

	tracker.loadingNewer = true;
	fetchNewerCallback(tracker, (rows, newestId) =>
	{
		prependRows(rows, tracker, list, newestId);
		tracker.loadingNewer = false;
	});
};

/**
 * Create a scroll event handler for the container.
 *
 * This handler ensures that loading is triggered only when the user is close
 * to the bottom/top of the container and prevents multiple concurrent loads.
 *
 * @param {object} container - The scrollable container.
 * @param {PaginationTracker} tracker - The pagination tracker.
 * @param {function} fetchCallback - Function to fetch data.
 * @param {string} [direction='down'] - Scroll direction: 'down' for bottom loading, 'up' for top loading.
 * @param {string} [listCache='list'] - The list cache name.
 * @returns {function} A scroll event handler function.
 */
export const createScrollHandler = (container, tracker, fetchCallback, direction = 'down', listCache = 'list') =>
{
	const canLoadFunc = direction === 'up' ? canLoadAtTop : canLoad;
	const isUpDirection = direction === 'up';

	return (e, parent, callBack) =>
	{
		const metrics = getScrollMetrics(container);

		// Always fetch on the initial call (callBack signals the onCreated load).
		// The scroll-position check only gates subsequent pagination loads triggered
		// by scroll events, not the first data fetch.
		if (callBack || canLoadFunc(metrics, tracker))
		{
			// Prevent multiple concurrent loads
			if (tracker.loading)
			{
				return;
			}

			tracker.loading = true;
			fetchCallback(tracker, (rows, lastCursor) =>
			{
				// Re-read the list reference here so it resolves after the async
				// fetch completes. When BiDirectionalContainer is used inside an
				// Atom (no intermediate Component scope), the child List Jot may
				// not yet have registered itself as parent[listCache] at the time
				// handleScroll is called synchronously during onCreated.
				const list = parent[listCache];

				if (callBack)
				{
					callBack();
				}

				// Check if this is initial load BEFORE updating (which increments offset)
				const isInitialLoad = tracker.currentOffset === 0;

				// Use appropriate update function based on direction
				if (isUpDirection)
				{
					// For 'up' direction, pass container to preserve scroll position
					updateRowsAtTop(rows, tracker, list, lastCursor, container);

					// Set newestId from initial load by detecting sort order
					if (isInitialLoad)
					{
						const newestId = getNewestId(rows);
						if (newestId !== null)
						{
							tracker.updateNewest(newestId);
						}
					}
				}
				else
				{
					updateRows(rows, tracker, list, lastCursor);
				}

				tracker.loading = false;
			});
		}
	};
};

/**
 * Create a table scroll event handler for the container.
 *
 * This handler ensures that loading is triggered only when the user is close
 * to the bottom of the container and prevents multiple concurrent loads.
 *
 * @param {object} container - The scrollable container.
 * @param {PaginationTracker} tracker - The pagination tracker.
 * @param {function} fetchCallback - Function to fetch data.
 * @returns {function} A scroll event handler function.
 */
export const createTableScrollHandler = (container, tracker, fetchCallback) =>
{
	return (e, list, callBack) =>
	{
		const metrics = getScrollMetrics(container);

		// Always fetch on the initial call.
		if (callBack || canLoad(metrics, tracker))
		{
			// Prevent multiple concurrent loads
			if (tracker.loading)
			{
				return;
			}

			tracker.loading = true;
			fetchCallback(tracker, (rows, lastCursor) =>
			{
				if (callBack)
				{
					callBack();
				}

				updateRows(rows, tracker, list, lastCursor);
				tracker.loading = false;
			});
		}
	};
};
